import { z } from 'zod';
import { ownerEmailSyncPayloadSchema } from './install-link';
import { LICENCE_PROTOCOL, LICENCE_REJECTION_REASONS, LICENCE_VERSION_PATTERN, signedLicenceSchema } from '../licence';
/* ------------------------------ vocabulary ------------------------------ */
export const DEPLOYMENT_MODES = ['CLOUD', 'LOCAL'];
export const LOCK_STATES = ['NONE', 'STALE', 'MISMATCH', 'VERSION'];
export const RELEASE_CHANNELS = ['STABLE', 'BETA'];
export const releaseChannelSchema = z.enum(RELEASE_CHANNELS);
/** What the cloud thinks of the install that is talking to it. */
export const ACTIVATION_STATES = ['NOT_ACTIVATED', 'ACTIVE', 'MISMATCH', 'RETIRED'];
export const OWNER_ALERT_TYPES = [
    'STALE_WARNING',
    'LOCKED',
    'UNLOCKED',
    'FINGERPRINT_MISMATCH',
    'UPDATE_FAILED',
    'CUSTOMER_SILENT',
    'NEW_VERSION',
    'SYNC_ROWS_PARKED',
    'STEP_UP_LOCKOUT',
    'OWNER_LOGIN_ATTACK',
    'SYNC_RESTORE_DETECTED',
    'SYNC_SEQUENCE_REGRESSION',
    'SYNC_PAUSED_CLOUD_OLDER',
    'TENANT_FROZEN',
    'TENANT_PURGE_FAILED',
    'LICENCE_REJECTED',
    'LICENCE_NOT_APPLIED',
    'LICENCE_VERSION_AHEAD',
    'LICENCE_KEY_PROBLEM',
    'PROFILE_CONFLICT',
    'FINGERPRINT_MISSING',
    'SUBSCRIPTION_ENDING',
    'LIFECYCLE_REPORT',
    'SUPPORT_SESSION_OPENED',
    'CLOSURE_SCHEDULED',
    'EXPORT_READY',
    'BRANCH_REQUESTED',
    'EMAIL_PAUSED',
];
/** Product identifier baked into release manifests and `.lpk` files. */
export const PRODUCT_ID = 'his';
export const PRODUCT_NAME = 'HIS';
/** Lock banner copy — fixed text per plan §3.5, do not paraphrase. */
export const LOCK_MESSAGES = {
    STALE: 'Read-only until this server syncs with the cloud. Connect to the internet — unlock is automatic.',
    MISMATCH: 'This install is activated for a different machine — contact support.',
    VERSION: 'A required update has not been applied — add/edit is disabled until this server is updated.',
};
/** 403 OFFLINE_NOT_ENABLED — the owner has not allowed offline mode for this hospital. */
export const OFFLINE_NOT_ENABLED_MESSAGE = 'Offline mode is not enabled for this hospital — ask the owner to allow it';
/** Shown on the cloud to staff of a hospital that runs a local server. */
export const CLOUD_MIRROR_MESSAGE = 'This hospital runs on its own local server. The cloud copy is read-only — make changes on the local server.';
const FACILITY_NOUNS = { hospital: 'hospital', lab: 'laboratory', pharmacy: 'pharmacy' };
export function facilityNoun(facilityType) {
    return FACILITY_NOUNS[facilityType ?? ''] ?? 'hospital';
}
export function cloudMirrorMessage(facilityType) {
    const noun = facilityNoun(facilityType);
    return `This ${noun} runs on its own server. This online copy is read-only — make changes on the ${noun}'s own server.`;
}
export function onlineCopyFreshness(currentAsOf, serverSeenAt, now = Date.now()) {
    const asOf = currentAsOf ? new Date(currentAsOf) : null;
    if (!asOf || Number.isNaN(asOf.getTime()))
        return { behind: false, asOf: null };
    const seen = serverSeenAt ? new Date(serverSeenAt).getTime() : Number.NaN;
    const serverOnRecently = Number.isFinite(seen) && now - seen <= 30 * 60 * 1000;
    return { behind: serverOnRecently && now - asOf.getTime() > 60 * 60 * 1000, asOf };
}
export function staleWarningMessage(daysRemaining) {
    const n = Math.max(0, daysRemaining);
    return `This system must connect to the internet within ${n} day${n === 1 ? '' : 's'} or it will become read-only.`;
}
/** Days before the lock at which the yellow warning starts (D2: warnings start at day 4 of 7). */
export const STALE_WARNING_DAYS = 3;
/** Semver-ish `a.b.c` compare: negative when a < b, 0 when equal. Accepts a
 * `-suffix` (beta) which sorts before the plain version. */
export function compareVersions(a, b) {
    const parse = (v) => {
        const [core = '', pre = ''] = v.trim().replace(/^v/i, '').split('-', 2);
        return { nums: core.split('.').map((n) => Number.parseInt(n, 10) || 0), pre };
    };
    const pa = parse(a);
    const pb = parse(b);
    for (let i = 0; i < Math.max(pa.nums.length, pb.nums.length); i++) {
        const diff = (pa.nums[i] ?? 0) - (pb.nums[i] ?? 0);
        if (diff !== 0)
            return diff;
    }
    if (pa.pre === pb.pre)
        return 0;
    if (!pa.pre)
        return 1;
    if (!pb.pre)
        return -1;
    return pa.pre < pb.pre ? -1 : 1;
}
export const versionSchema = z
    .string()
    .trim()
    .regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.]+)?$/, 'version must look like 3.4.0 or 3.4.0-beta.1');
/* ------------------------------- ingest -------------------------------- */
export const syncIngestEntrySchema = z.object({
    sequenceNo: z.union([z.string(), z.number()]).transform((v) => String(v)),
    tableName: z.string().min(1).max(80),
    rowId: z.string().min(1).max(80),
    operation: z.enum(['INSERT', 'UPDATE', 'DELETE']),
    payload: z.record(z.unknown()).nullable(),
});
export const syncIngestBodySchema = z.object({
    entries: z.array(syncIngestEntrySchema).max(1000),
});
export const SYNC_VERDICT_CODES = ['FK_MISSING', 'UNKNOWN_TABLE', 'UNKNOWN_COLUMN', 'CONSTRAINT', 'DATA', 'RLS', 'PAYLOAD', 'RETRY_LIMIT'];
/* ------------------------------ check-in ------------------------------ */
/** Body of `POST /api/v1/sync/checkin` (local → cloud). Every field optional so
 * an older local install keeps working against a newer cloud. */
export const SYNC_REPAIR_MAX_IDS = 5_000;
export const OWNER_COMMAND_KINDS = [
    'user.set_active',
    'user.reset_password',
    'user.reset_2fa',
    'user.force_logout',
    'user.unblock',
    'user.set_watch_allowed',
    'live_session.open',
    'live_session.close',
    'tenant.email_sync',
    'sync.requeue_parked',
    'sync.compare',
    'sync.recapture',
    'sync.redelete',
];
const commandUserId = z.string().min(1).max(80);
export const ownerCommandPayloadSchemas = {
    'user.set_active': z.object({ userId: commandUserId, isActive: z.boolean() }),
    'user.reset_password': z.object({ userId: commandUserId, passwordHash: z.string().min(20).max(300) }),
    'user.reset_2fa': z.object({ userId: commandUserId }),
    'user.force_logout': z.object({ userId: commandUserId, blockMinutes: z.number().int().min(0).max(1440).optional() }),
    'user.unblock': z.object({ userId: commandUserId }),
    'user.set_watch_allowed': z.object({ userId: commandUserId, allowed: z.boolean() }),
    'live_session.open': z.object({
        sessionId: z.string().uuid(),
        codeHash: z.string().regex(/^[0-9a-f]{64}$/),
        userId: commandUserId,
        ownerId: z.string().min(1).max(80),
        ownerName: z.string().min(1).max(120),
        reason: z.string().min(1).max(400),
        endsAt: z.string().datetime(),
    }),
    'live_session.close': z.object({ sessionId: z.string().uuid(), endsAt: z.string().datetime() }),
    'tenant.email_sync': ownerEmailSyncPayloadSchema,
    'sync.requeue_parked': z.object({
        tables: z.array(z.string().min(1).max(80)).max(200).optional(),
        codes: z.array(z.enum(SYNC_VERDICT_CODES)).max(SYNC_VERDICT_CODES.length).optional(),
    }),
    'sync.compare': z.object({
        tables: z.array(z.string().min(1).max(80)).max(300).optional(),
        sinceSeq: z.string().regex(/^\d{1,19}$/).optional(),
    }),
    'sync.recapture': z.object({ table: z.string().min(1).max(80), ids: z.array(z.string().min(1).max(80)).min(1).max(SYNC_REPAIR_MAX_IDS) }),
    'sync.redelete': z.object({ table: z.string().min(1).max(80), ids: z.array(z.string().min(1).max(80)).min(1).max(SYNC_REPAIR_MAX_IDS) }),
};
export const ownerCommandResultSchema = z.object({
    id: z.string().min(1).max(80),
    status: z.enum(['applied', 'failed']),
    message: z.string().max(300).optional(),
});
export const syncLocalProfileSchema = z.object({
    logoUrl: z.string().max(500).nullable().optional(),
    primaryColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .optional(),
    country: z.string().length(2).nullable().optional(),
    timezone: z.string().min(1).max(64).optional(),
    locale: z.string().min(2).max(12).optional(),
    currency: z.string().length(3).optional(),
});
export const syncCheckinBodySchema = z.object({
    runningVersion: z.string().trim().max(40).optional(),
    profile: syncLocalProfileSchema.optional(),
    oldestPendingAt: z.string().datetime().optional(),
    commandResults: z.array(ownerCommandResultSchema).max(50).optional(),
    hardwareFingerprint: z.string().trim().length(64).optional(),
    hostname: z.string().trim().max(120).optional(),
    outboxBacklog: z.number().int().min(0).optional(),
    outboxParked: z.number().int().min(0).optional(),
    outboxStuck: z.number().int().min(0).optional(),
    lastUpdateFailed: z.boolean().optional(),
    lastUpdateLog: z.string().max(4000).optional(),
    diskFreeMb: z.number().int().min(0).optional(),
    dbSizeMb: z.number().int().min(0).optional(),
    apiUptimeSec: z.number().int().min(0).optional(),
    licenceProtocol: z.literal(LICENCE_PROTOCOL).optional(),
    licenceVersion: z.string().regex(LICENCE_VERSION_PATTERN).optional(),
    licenceRejected: z
        .object({ version: z.string().regex(LICENCE_VERSION_PATTERN), reason: z.enum(LICENCE_REJECTION_REASONS) })
        .nullable()
        .optional(),
    heldLicence: signedLicenceSchema.optional(),
    lastSignatureOkAt: z.string().datetime().nullable().optional(),
    profileConflict: z.string().max(200).nullable().optional(),
    localExportAt: z.string().datetime().nullable().optional(),
    ownerEmailVersion: z.string().max(40).optional(),
    scheduledUpdate: z
        .object({ version: versionSchema, at: z.string().datetime(), automatic: z.boolean() })
        .nullable()
        .optional(),
    sync: z
        .object({
        backlogLive: z.number().int().min(0).optional(),
        backlogBulk: z.number().int().min(0).optional(),
        oldestPendingLiveAt: z.string().datetime().nullable().optional(),
        parkedByCode: z.record(z.string().max(40), z.number().int().min(0)).optional(),
        filesWaiting: z.number().int().min(0).optional(),
        filesWaitingBytes: z.number().int().min(0).optional(),
        filesGivenUp: z.number().int().min(0).optional(),
        pausedReason: z.string().max(40).nullable().optional(),
        minUnsentSeq: z.string().regex(/^\d{1,19}$/).nullable().optional(),
        restoreDetectedAt: z.string().datetime().nullable().optional(),
        lastAckSeq: z.string().regex(/^\d{1,19}$/).nullable().optional(),
    })
        .optional(),
});
/* ------------------------------ activation ------------------------------ */
/** Body of `POST /api/v1/sync/enroll` — the one online activation call the
 * installer makes (plan §3.4). */
export const syncEnrollBodySchema = z
    .object({
    token: z.string().trim().min(20).max(200).optional(),
    licenseKey: z.string().trim().min(36).max(40).optional(),
    hardwareFingerprint: z.string().trim().length(64),
    hostname: z.string().trim().max(120),
    runningVersion: z.string().trim().max(40),
})
    .refine((body) => (body.token === undefined) !== (body.licenseKey === undefined), {
    message: 'Send either an activation token or a licence key, not both and not neither',
    path: ['token'],
});
export const SNAPSHOT_PAGE_DEFAULT_LIMIT = 1000;
export const SNAPSHOT_PAGE_MAX_LIMIT = 2000;
export const snapshotPageQuerySchema = z.object({
    after: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(SNAPSHOT_PAGE_MAX_LIMIT).default(SNAPSHOT_PAGE_DEFAULT_LIMIT),
});
/* ------------------------------- updates ------------------------------- */
export const updatesLatestQuerySchema = z.object({
    channel: releaseChannelSchema.optional(),
    current: z.string().trim().max(40).optional(),
});
/** Landing-page download auth: customer code (tenant slug) + download PIN (plan §3.2). */
export const downloadAuthSchema = z.object({
    tenantSlug: z.string().trim().min(2).max(40),
    pin: z.string().trim().regex(/^\d{6}$/, 'PIN is 6 digits'),
});
/**
 * How the landing page hands the slug + PIN to `GET /updates/latest` and
 * `GET /updates/download/:version`: headers for the XHR check, query-string
 * keys for the browser-navigated download (a plain `<a>`/`location.assign`
 * cannot set headers). Either form is accepted by the API.
 */
export const DOWNLOAD_AUTH_HEADERS = { tenantSlug: 'x-tenant-slug', pin: 'x-download-pin' };
export const DOWNLOAD_AUTH_QUERY = { tenantSlug: 'tenantSlug', pin: 'pin' };
const compareTable = z.string().min(1).max(80);
const compareBucket = z.string().regex(/^[0-9a-f]$/);
const compareIds = z.array(z.string().min(1).max(80)).max(SYNC_REPAIR_MAX_IDS);
export const syncCompareBodySchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('digests'),
        sinceSeq: z.string().regex(/^\d{1,19}$/).optional(),
        tables: z.array(z.object({ table: compareTable, buckets: z.record(compareBucket, z.string().regex(/^[0-9a-f]{32}$/)) })).max(400),
    }),
    z.object({ phase: z.literal('rows'), table: compareTable, buckets: z.array(compareBucket).min(1).max(16), after: z.string().max(80).optional() }),
    z.object({
        phase: z.literal('report'),
        sinceSeq: z.string().regex(/^\d{1,19}$/).optional(),
        tablesCompared: z.number().int().min(0).max(1000),
        tables: z
            .array(z.object({ table: compareTable, missingOnline: compareIds, different: compareIds, onlyOnline: compareIds, deletedOnline: compareIds.default([]), truncated: z.boolean() }))
            .max(400),
    }),
]);
export const ownerSyncRepairSchema = z.object({
    totpCode: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator'),
    table: compareTable,
    action: z.enum(['recapture', 'redelete']),
    ids: z.array(z.string().min(1).max(80)).min(1).max(SYNC_REPAIR_MAX_IDS),
});
export const HEALABLE_SYNC_CODES = ['FK_MISSING', 'RETRY_LIMIT', 'UNKNOWN_TABLE', 'UNKNOWN_COLUMN'];
export const ONLINE_COPY_TEXT = {
    'onlineCopy.title': 'Online copy',
    'onlineCopy.safeHere': 'Your data is safe on this server. This panel is only about the online copy.',
    'onlineCopy.upToDate': 'The online copy is up to date.',
    'onlineCopy.hasEverythingUpTo': 'The online copy has everything up to {time}.',
    'onlineCopy.liveWaiting': 'Patient and billing work waiting: {n}',
    'onlineCopy.bulkWaiting': 'Logs and history waiting: {n} (sent when the online service is quiet)',
    'onlineCopy.filesWaiting': 'Documents waiting: {n} ({size})',
    'onlineCopy.filesWaitingImaging': 'Documents and images waiting: {n} ({size})',
    'onlineCopy.filesGivenUp': 'Documents that could not be sent: {n}',
    'onlineCopy.notAccepted': 'Not accepted by the online copy: {counts} — your provider has been told.',
    'onlineCopy.pausedCloudOlder': 'Paused: waiting for your provider to update the online service.',
    'onlineCopy.restored': 'This server was restored from an older backup on {date}. Some newer work may exist only in the online copy — contact your provider.',
    'onlineCopy.lastCompared': 'Last compared with the online copy: {date} — {result}',
    'onlineCopy.compareMatched': 'everything matched',
    'onlineCopy.compareDifferences': '{n} differences',
    'onlineCopy.sendNow': 'Send to the online copy now',
    'onlineCopy.tryAgain': 'Try again',
    'onlineCopy.banner.behind': 'The online copy is {n} h behind.',
    'onlineCopy.banner.paused': 'Sending to the online copy is paused.',
    'onlineCopy.banner.notAccepted': 'Some changes were not accepted by the online copy.',
    'onlineCopy.banner.restored': 'This server was restored from an older backup.',
    'onlineCopy.reason.waitingForLinked': 'waiting for a linked record',
    'onlineCopy.reason.needsNewerService': 'needs a newer online service',
    'onlineCopy.reason.rule': 'conflicts with a rule',
    'onlineCopy.reason.value': 'has a value the online copy cannot hold',
    'onlineCopy.reason.other': 'not accepted',
};
export function onlineCopyText(key, values = {}) {
    return ONLINE_COPY_TEXT[key].replace(/\{(\w+)\}/g, (whole, name) => (name in values ? String(values[name]) : whole));
}
export const updatesApplyBodySchema = z.object({
    /** Version to apply; must equal `pendingUpdate.version`. */
    version: versionSchema,
    /** Optional path to an already-downloaded `.lpk` on the server machine. */
    localPath: z.string().trim().max(500).optional(),
});
export const ownerUpdateDeploymentBodySchema = z.object({
    totpCode: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator'),
    graceDays: z.number().int().min(1).max(365).optional(),
    updateRequired: z.boolean().optional(),
    offlineEnabled: z.boolean().optional(),
});
/** Token-issuing, PIN and retire actions carry the operator's live code only. */
export const ownerDeploymentActionSchema = z.object({
    totpCode: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator'),
});
export const ownerRequeueParkedSchema = ownerDeploymentActionSchema.extend({
    tables: z.array(z.string().min(1).max(80)).max(200).optional(),
    codes: z.array(z.enum(SYNC_VERDICT_CODES)).max(SYNC_VERDICT_CODES.length).optional(),
});
export const ownerReleaseUploadFieldsSchema = z.object({
    channel: releaseChannelSchema.default('STABLE'),
    notes: z.string().max(20_000).optional(),
    isRequired: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .optional(),
    minimumAllowedVersion: versionSchema.optional(),
    requiredBy: z.string().datetime().optional(),
});
export const ownerReleaseAnnounceSchema = z.object({
    version: versionSchema,
    channel: releaseChannelSchema.default('STABLE'),
    notes: z.string().max(20_000).optional(),
    isRequired: z.boolean().optional(),
    requiredBy: z.string().datetime().optional(),
});
export const ownerReleaseUpdateBodySchema = z.object({
    notes: z.string().max(20_000).optional(),
    isRequired: z.boolean().optional(),
    minimumAllowedVersion: versionSchema.nullable().optional(),
    requiredBy: z.string().datetime().nullable().optional(),
});
export const ALERT_SMTP_ENCRYPTIONS = ['NONE', 'STARTTLS', 'SSL_TLS'];
export const ownerAlertSettingsBodySchema = z.object({
    smtpHost: z.string().trim().max(200).nullable().optional(),
    smtpPort: z.number().int().min(1).max(65535).nullable().optional(),
    smtpUser: z.string().trim().max(200).nullable().optional(),
    /** Omit to keep the stored password; empty string clears it. */
    smtpPassword: z.string().max(500).optional(),
    smtpFrom: z.string().trim().max(200).nullable().optional(),
    smtpEncryption: z.enum(ALERT_SMTP_ENCRYPTIONS).optional(),
    smsGatewayUrl: z.string().trim().url().max(500).nullable().optional(),
    /** Omit to keep the stored key; empty string clears it. */
    smsGatewayApiKey: z.string().max(500).optional(),
    smsSenderId: z.string().trim().max(40).nullable().optional(),
    ownerEmails: z.array(z.string().trim().email().max(254)).max(20).optional(),
    ownerPhones: z.array(z.string().trim().max(20)).max(20).optional(),
    notifyCustomerAdmin: z.boolean().optional(),
    warnDaysBefore: z.number().int().min(1).max(30).optional(),
    silentAfterHours: z.number().int().min(1).max(720).optional(),
    paymentGraceDays: z.number().int().min(0).max(60).optional(),
    escrowRetentionDays: z.number().int().min(7).max(3650).optional(),
    lifecycleSweepMode: z.enum(['report', 'enforce']).optional(),
});
/** The PUT carries the operator's live code alongside the settings. */
export const ownerAlertSettingsRequestSchema = ownerAlertSettingsBodySchema.extend({
    totpCode: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator'),
});
export const SYNC_STATES = ['not_activated', 'locked', 'offline', 'needs_attention', 'up_to_date', 'syncing', 'behind'];
export const SERVER_STRIP_CACHE_MS = 5 * 60 * 1000;
export const SERVER_STRIP_FORCE_SPACING_MS = 30_000;
export const ownerServerStripRefreshSchema = z.object({ totpCode: z.string().trim().regex(/^\d{6}$/) });
export const ownerSyncStatusQuerySchema = z.object({
    facilityType: z.enum(['hospital', 'lab', 'pharmacy']).optional(),
    state: z.enum(SYNC_STATES).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: z.string().max(80).optional(),
});
export const ownerAlertsListQuerySchema = z.object({
    tenantId: z.string().optional(),
    type: z.enum(OWNER_ALERT_TYPES).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const releaseManifestSchema = z.object({
    product: z.string().min(1),
    version: versionSchema,
    channel: releaseChannelSchema,
    gitSha: z.string(),
    builtAt: z.string(),
    minUpgradeFrom: z.string(),
    migrations: z.array(z.string()),
    files: z.record(z.string()),
    payloadSha256: z.string().length(64),
    payloadSize: z.number().int().min(0),
    outerEntries: z.record(z.string()).optional(),
    checkinKeyIds: z.array(z.string()).optional(),
});
//# sourceMappingURL=sync.js.map